/**
 * AdminAnalyticsTrafficPage — first-party traffic dashboard.
 *
 * Feb 2026 Analytics Commit C2. Reads from
 * `GET /api/admin/analytics/dashboard?range=today|7d|30d`.
 *
 * Layout (per design doc §2 — BigCommerce reference):
 *   1. Header + date-range selector
 *   2. 4 KPI cards row: Visits / Daily uniques (summed) / Waitlist
 *      signups / Contest entries — clickable to change the trend-
 *      chart metric
 *   3. Trend chart (single metric)
 *   4. Top Pages + Sources tables side-by-side
 *   5. Locked Paid Ads stub tile
 *
 * Route: /admin/analytics/traffic (nested inside AdminShell).
 * Route naming decision documented in the Commit C1 message.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import DateRangeSelector from "@/components/admin/analytics/DateRangeSelector";
import KpiCard from "@/components/admin/analytics/KpiCard";
import TrendChart from "@/components/admin/analytics/TrendChart";
import TopPagesTable from "@/components/admin/analytics/TopPagesTable";
import SourcesTable from "@/components/admin/analytics/SourcesTable";
import LockedPaidAdsTile from "@/components/admin/analytics/LockedPaidAdsTile";

const KPI_ORDER = [
  { key: "visits", label: "Visits", testIdSuffix: "visits" },
  // Feb 2026 Lane B — label renamed from "Unique visitors" to
  // "Daily uniques (summed)" to make the arithmetic honest. The
  // `analytics_rollups_daily` collection key includes `path`, so
  // SUM(unique_visitors) across the window over-counts anyone who
  // touched multiple paths on the same day. The renamed label
  // discloses the summing to the operator without pretending it's
  // a distinct-visitor count. A tighter daily-unique rollup
  // (materialize MIN-cardinality visitor_hash sets per date,
  // path-independent) is DEFERRED per Commit C's operator-notable
  // Decision B — see `backend/analytics_dashboard.py` module
  // header for the deferral rationale.
  { key: "unique_visitors", label: "Daily uniques (summed)", testIdSuffix: "unique-visitors" },
  { key: "waitlist_signups", label: "Waitlist signups", testIdSuffix: "waitlist-signups" },
  { key: "contest_entries", label: "Contest entries", testIdSuffix: "contest-entries" },
];

export default function AdminAnalyticsTrafficPage() {
  const [range, setRange] = useState("7d");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState("visits");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/analytics/dashboard", {
        params: { range },
      });
      setPayload(data);
    } catch (e) {
      setError("Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = payload?.kpis || {};
  const firstRollupDate = payload?.first_rollup_date || null;

  return (
    <div
      className="space-y-6"
      data-testid="admin-analytics-traffic-page"
      data-range={range}
      data-loading={loading ? "true" : "false"}
    >
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ color: "var(--pp-text)" }}
          >
            Traffic
          </h1>
          <p
            className="mt-1 text-sm"
            style={{ color: "var(--pp-text-soft)" }}
          >
            First-party visits, funnel signups, and top sources.
          </p>
        </div>
        <DateRangeSelector value={range} onChange={setRange} />
      </header>

      {error ? (
        <Card
          style={{
            background: "var(--pp-v3-surface)",
            borderColor: "var(--pp-border)",
          }}
        >
          <CardContent className="p-6" data-testid="analytics-error">
            <div className="text-sm" style={{ color: "var(--pp-text)" }}>
              {error}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <section
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="analytics-kpi-row"
      >
        {KPI_ORDER.map((k) => (
          <KpiCard
            key={k.key}
            label={k.label}
            tile={kpis[k.key]}
            testIdSuffix={k.testIdSuffix}
            active={selectedMetric === k.key}
            onClick={() => setSelectedMetric(k.key)}
          />
        ))}
      </section>

      <section>
        <Card
          style={{
            background: "var(--pp-v3-surface)",
            borderColor: "var(--pp-border)",
          }}
        >
          <CardContent className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2
                className="text-sm font-bold uppercase tracking-[0.06em]"
                style={{ color: "var(--pp-text-soft)" }}
              >
                Daily trend — {KPI_ORDER.find((k) => k.key === selectedMetric)?.label}
              </h2>
            </div>
            <TrendChart
              timeseries={payload?.timeseries || []}
              metric={selectedMetric}
              firstRollupDate={firstRollupDate}
            />
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          style={{
            background: "var(--pp-v3-surface)",
            borderColor: "var(--pp-border)",
          }}
        >
          <CardContent className="p-5">
            <h2
              className="mb-3 text-sm font-bold uppercase tracking-[0.06em]"
              style={{ color: "var(--pp-text-soft)" }}
            >
              Top Pages
            </h2>
            <TopPagesTable
              rows={payload?.top_pages || []}
              firstRollupDate={firstRollupDate}
            />
          </CardContent>
        </Card>

        <Card
          style={{
            background: "var(--pp-v3-surface)",
            borderColor: "var(--pp-border)",
          }}
        >
          <CardContent className="p-5">
            <h2
              className="mb-3 text-sm font-bold uppercase tracking-[0.06em]"
              style={{ color: "var(--pp-text-soft)" }}
            >
              Sources
            </h2>
            <SourcesTable
              rows={payload?.sources || []}
              firstRollupDate={firstRollupDate}
            />
          </CardContent>
        </Card>
      </section>

      <section
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="analytics-locked-row"
      >
        <LockedPaidAdsTile />
      </section>
    </div>
  );
}
